import { ResponseChoiceType } from "@/lib/types/personality";
import { QuestionType } from "@/lib/utils/mpt";
import BulletSlider from "@/components/V2/Testdrive/BulletSlider";
import { useDebounce } from "@/hooks/useDebounce";
import { useEffect, useState } from "react";

const RadioList = ({
	defaultSelected,
	choices,
	id,
	onSelect,
}: {
	defaultSelected?: any;
	choices: Array<ResponseChoiceType>;
	id: string;
	onSelect: (val: number) => void;
}) => {
	return (
		<ul className="ul pl-0 list-style-none flex flex-direction-column gap-1 fs-14 fw-500 font-secondary plr-2 ">
			{choices.map(({ value, text }, i) => (
				<li key={`${text}-${id}`}>
					<label
						className={`flex label justify-content-space-between align-items-flex-start ${
							value == defaultSelected ? `active` : ``
						}`}
						htmlFor={`${text}-${id}`}
						onClick={() => onSelect(value)}
					>
						<div className="choice">{text}</div>
						<input
							type="radio"
							name={`${text}-${id}`}
							className="radio cursor-pointer"
							checked={defaultSelected == value}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									onSelect(value);
								}
							}}
						/>
					</label>
				</li>
			))}
			<style jsx>{`
				.ul {
					overflow-y: auto;
				}
				.label {
					width: 100%;
					color: var(--primary-300);
					cursor: pointer;
				}
				.label:hover {
					color: var(--primary-400);
				}
				.active {
					color: var(--primary-500);
				}
				.choice {
					flex: 0 0 80%;
				}
				.radio {
					accent-color: var(--primary-300);
					border-color: var(--primary-300);
				}
				.radio:checked {
					accent-color: var(--primary-500);
					border-color: var(--primary-500);
				}
			`}</style>
		</ul>
	);
};

const HorizontalRangeSlider = ({
	defaultSelected,
	choices,
	id,
	onSelect,
	color,
}: {
	defaultSelected: number;
	choices: ResponseChoiceType[];
	id: string;
	onSelect: (val: number) => void;
	color: string;
}) => {
	const [selectedValue, setSelectedValue] = useState(defaultSelected);
	const debouncedValue = useDebounce(selectedValue, 500);

	useEffect(() => {
		if (debouncedValue) {
			onSelect(debouncedValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	return (
		<>
			<div className="pt-7">
				<BulletSlider
					defaultStep={selectedValue}
					steps={choices.map((v) => ({
						text: v.text,
						value: v.value,
					}))}
					points={{
						size: "0",
					}}
					onValueChange={setSelectedValue}
					trackLine={{
						lineColor: `var(--${color}-200)`,
						progressLineColor: `var(--${color}-200)`,
						height: `6px`,
					}}
					thumbnail={{
						backgroundColor: `var(--${color}-200)`,
						borderColor: `var(--${color}-500)`,
						borderWidth: `2px`,
						showThumbnailText: true,
					}}
					ticks={{
						hideTick: false,
					}}
				/>
			</div>
		</>
	);
};

const Selection = ({
	question,
	onSelect,
}: {
	question: QuestionType;
	onSelect: (val: number) => void;
}) => {
	const { type, choices, id, answer, color } = question;
	switch (type) {
		case 2:
			return (
				<RadioList
					defaultSelected={answer}
					choices={choices}
					id={`${id}`}
					onSelect={onSelect}
				/>
			);
		case 6:
			return (
				<HorizontalRangeSlider
					defaultSelected={Number(answer)}
					choices={choices}
					id={`${id}`}
					onSelect={onSelect}
					color={color!}
				/>
			);
		default:
			return (
				<RadioList
					defaultSelected={answer}
					choices={choices}
					id={`${id}`}
					onSelect={onSelect}
				/>
			);
	}
};

const Question = ({
	questionIndex,
	question,
	color,
	onSelect,
}: {
	questionIndex?: number;
	question: QuestionType;
	color: string;
	onSelect?: (val: number) => void;
}) => {
	if (question.type === 0) {
		return <></>;
	}
	return (
		<>
			<div className="card plr-4 ptb-3 mtb-2">
				<div className="fs-12 fw-500 font-tertiary color-abyssal-blue-300 mb-1">
					Question {questionIndex && `${questionIndex}`}
				</div>
				<div className="fs-14 fw-500 font-secondary color-abyssal-blue-400 text pb-3">
					{question.text}
				</div>
				<div>
					<Selection question={question} onSelect={onSelect!} />
				</div>
			</div>
			<style jsx>{`
				.card {
					background: var(--${color}-100);
					box-shadow: -4px 4px 0px 0px var(--${color}-500);
					border: 1px solid var(--${color}-500);
					border-radius: 20px;
					overflow-y: auto;
					height: 90%;
				}
				.text {
					border-bottom: 1px solid var(--${color}-200);
				}
			`}</style>
		</>
	);
};

export default Question;
